import {
  Box,
  Container,
  Paper,
  Typography,
  Button as _LoadingButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { object, string, TypeOf } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormInput } from '../share';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useChangePasswordMutation } from '../apis/Auth.api';
import { ChangePasswordRequest } from '../types/auth.d';
import { useDynamicSnackbar } from '../share';
import { useTitle } from 'react-use';
import { Trans } from '@lingui/react/macro';
import { t, msg } from '@lingui/core/macro';

const LoadingButton = styled(_LoadingButton)`
  padding: 0.6rem 0;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
  }
`;

const handleError = (actionError: any, openFailSnackbar) => {
  if (Array.isArray(actionError?.data?.error)) {
    actionError.data.error.forEach((el: any) => {
      openFailSnackbar(el.message);
    });
  } else if (actionError?.data?.message) {
    openFailSnackbar(actionError.data.message);
  } else {
    console.log('Unknown error', actionError);
    openFailSnackbar(actionError.data.message);
  }
};

const changePasswordSchema = object({
  oldPassword: string()
    .trim()
    .min(1, msg`Old password is required`)
    .min(8, msg`Old password must be more than 8 characters`),
  newPassword: string()
    .trim()
    .min(1, msg`New password is required`)
    .min(8, msg`New password must be more than 8 characters`),
  newPasswordConfirm: string()
    .trim()
    .min(1, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.newPasswordConfirm, {
  message: msg`Passwords do not match`,
  path: ['passwordConfirm'],
});

export type ChangePasswordPage = TypeOf<typeof changePasswordSchema>;

const ChangePasswordPage = () => {
  useTitle(t`Change Password`);

  const methods = useForm<ChangePasswordRequest>({
    resolver: zodResolver(changePasswordSchema),
  });

  const [changePassword, { isLoading, isError, error, isSuccess, data }] =
    useChangePasswordMutation();

  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = methods;

  const { openSuccessSnackbar, openFailSnackbar } = useDynamicSnackbar();

  useEffect(() => {
    if (isSuccess) {
      navigate('/');
      openSuccessSnackbar(t`Password updated successfully, login`);
    }
    if (isError) {
      handleError(error, openFailSnackbar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const onSubmitHandler: SubmitHandler<ChangePasswordPage> = async (values) => {
    try {
      const resp = await changePassword({ ...values });
      console.log('changePassword', resp);
    } catch (e) {
      handleError(e, openFailSnackbar);
    }
  };

  return (
    <Paper sx={{ padding: '2em' }} data-testid="ChangePassword-page">
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
           
            component="h2"
            variant="h2"
            data-testid="ChangePassword-title" sx={{ textAlign: "center" }}>
            <Trans>Change Password</Trans>
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              width: '100%',
              textAlign: 'center',
              mb: '1rem',
            }}
            data-testid="ChangePassword-subtitle"
          >
            <Trans>Please enter a new password</Trans>
          </Typography>
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmitHandler)}
              noValidate
              autoComplete="off"
              maxWidth="27rem"
              width="100%"
              sx={{
                p: { xs: '1rem', sm: '2rem' },
                borderRadius: 2,
              }}
              data-testid="ChangePassword-form"
            >
              <LoginFormInput
                name="oldPassword"
                label={t`Old Password`}
                type="password"
                inputProps={{
                  'data-testid': 'ChangePassword-oldPassword-input',
                }}
              />
              <LoginFormInput
                name="newPassword"
                label={t`New Password`}
                type="password"
                inputProps={{
                  'data-testid': 'ChangePassword-newPassword-input',
                }}
              />
              <LoginFormInput
                name="newPasswordConfirm"
                label={t`Confirm New Password`}
                type="password"
                inputProps={{
                  'data-testid': 'ChangePassword-newPasswordConfirm-input',
                }}
              />

              <LoadingButton
                variant="contained"
                sx={{ mt: 1 }}
                fullWidth
                disableElevation
                type="submit"
                loading={isLoading}
                data-testid="ChangePassword-submit-button"
              >
                <Trans>Change Password</Trans>
              </LoadingButton>
            </Box>
          </FormProvider>
        </Box>
      </Container>
    </Paper>
  );
};

export default ChangePasswordPage;
